import { parseISO, isValid } from 'date-fns';
import {
  AppliedJobsResponse,
  AvailabilityEntry,
  TransformedJobItem,
  ViewsTypesEnum,
  MyJobsTabsEnum,
  JobIdSource,
  JobAssignmentStatus,
  JobsConfigFormValues,
  NotificationFrequency,
  NotificationChannels,
  JobRecommendationResponse,
  SavedJobsResponse,
  NormalizedJobDetail,
  WorkerAvailabilityResponse,
  WorkerJobCommonResultsResponse,
  JobApplication,
  JobAssignment,
  WorkerJobCommonResultDTO,
} from '@worker/types/myJobs.type';
import { SearchFilters, AdvancedFiltersFormValues } from '@worker/stores/types';
import { CountryName } from '@th-types/address.type';
import { MY_JOBS_PAGE_SIZE } from '@constants/various';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * Build full job page URL from a job's publicUrl path.
 * Returns null if publicUrl is not provided.
 *
 * @example
 * buildJobPageUrl('/jobs/123') // => 'https://app.trustedherd.com/jobs/123'
 * buildJobPageUrl(null) // => null
 */
export function buildJobPageUrl(
  publicUrl: string | null | undefined
): string | null {
  if (!publicUrl) return null;
  return `${import.meta.env.VITE_SERVER_BASE_URL}${publicUrl}`;
}

/**
 * Build the URL to view a job, preferring the worker job assignment page
 * over the public company job page when a jobAssignmentId is available.
 *
 * @example
 * buildJobViewUrl(123, '/jobs/456') // => 'https://…/workerJobs/jobDetail?jobAssignmentId=123'
 * buildJobViewUrl(null, '/jobs/456') // => 'https://…/jobs/456'
 */
export function buildJobViewUrl(
  jobAssignmentId: number | null | undefined,
  publicUrl: string | null | undefined
): string | null {
  if (jobAssignmentId) {
    return `${import.meta.env.VITE_SERVER_BASE_URL}/workerJobs/jobDetail?jobAssignmentId=${jobAssignmentId}`;
  }
  return buildJobPageUrl(publicUrl);
}

/** Maps API preferences to form values */
export function mapApiResponseToFormValues(
  data: {
    notificationFrequency: NotificationFrequency;
    notificationChannels?: NotificationChannels[];
    preferredJobTitles?: { id: number; name: string }[];
    minPayRate?: number;
    allowSuggestedAppearance?: boolean;
  },
  workerAddress: string,
  defaultAddress: string
): JobsConfigFormValues {
  return {
    frequency: data.notificationFrequency,
    receiveChannels: data.notificationChannels || [],
    jobTitles:
      data.preferredJobTitles?.map((jobTitle) => String(jobTitle.id)) || [],
    minHourlyEnabled: !!data.minPayRate,
    minHourlyRate: data.minPayRate,
    minDailyEnabled: false,
    minDailyRate: undefined,
    currentAddress: workerAddress || defaultAddress,
    distance: 100,
    preferredAddresses: [],
    excludeCompany: [],
    allowSuggestedAppearance: data.allowSuggestedAppearance ?? true,
  };
}

/** Default MyJobs store state (used for tests/resets) */
export function getDefaultMyJobsState() {
  return {
    showWelcome: true,
    showAdvancedFilters: false,
    showConfigureModal: false,
    showGetInNetworkModal: false,
    currentView: ViewsTypesEnum.MY_JOBS,
    currentTab: MyJobsTabsEnum.NEW,
    searchFilters: {
      companyName: '',
      companyId: null,
      address: null,
      miles: null,
    },
    advancedFilters: {
      startDate: null,
      endDate: null,
      onGoing: false,
      jobLocation: {
        country: '' as '' | CountryName,
        includeNational: true,
      },
      jobTitles: [],
      minHourlyEnabled: false,
      minHourlyRate: undefined,
      minDailyEnabled: false,
      minDailyRate: undefined,
    },
    pagination: {
      currentPage: 1,
      itemsPerPage: MY_JOBS_PAGE_SIZE,
    },
  };
}

// ============================================================================
// URL Search Params Utilities
// ============================================================================

/**
 * Builds URLSearchParams from an object for Grails-compatible serialization.
 * Handles arrays by appending multiple values with the same key.
 * Filters out null/undefined values.
 */
export function buildURLSearchParams<T extends object | undefined>(
  params: T
): URLSearchParams {
  const searchParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(key, String(item)));
      } else if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
  }

  return searchParams;
}

export enum MyJobsQueryParamsEnum {
  // Basic search filters
  COMPANY_NAME = 'companyName',
  COMPANY_ID = 'companyId',
  ADDRESS = 'address',
  ADDRESS_LAT = 'lat',
  ADDRESS_LNG = 'lng',
  MILES = 'miles',

  // Sort/Order
  SORT = 'sort',
  ORDER = 'order',

  // Advanced filters
  JOB_TITLES = 'titles',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  ON_GOING = 'onGoing',
  MIN_HOURLY_RATE = 'minHourly',
  MIN_DAILY_RATE = 'minDaily',
  LOCATION_COUNTRY = 'country',
  LOCATION_INCLUDE_NATIONAL = 'includeNational',

  // Pagination
  OFFSET = 'offset',
  MAX = 'max',

  // Modal triggers
  OPEN_IN_NETWORK_BENEFITS = 'openInNetworkBenefits',
  OPEN_CONFIGURE = 'openConfigure',
  OPEN_JOB_DETAILS = 'openJobDetails',
}

export interface MyJobsUrlParams {
  searchFilters?: Partial<SearchFilters>;
  advancedFilters?: Partial<AdvancedFiltersFormValues>;
  sort?: string;
  order?: string;
  offset?: number;
  max?: number;
  openJobDetails?: number | null;
}

export interface MyJobsApiQueryParams {
  // Pagination
  max?: number;
  offset?: number;

  // Basic filters - companyIds supports array for Grails multi-value params
  companyIds?: string | string[];
  address?: string;
  addressLatitude?: number;
  addressLongitude?: number;
  maxMilesAway?: number;

  // Sort
  sort?: string;
  order?: 'asc' | 'desc';

  // Advanced filters - titleIds supports array for Grails multi-value params
  titleIds?: string | string[];
  startDate?: string;
  endDate?: string;
  onGoing?: boolean;
  minimumHourlyRate?: number;
  minimumDailyRate?: number;
  location?: string;
  includeNationalJob?: boolean;
}

function parseOpenJobDetailsId(raw: string | null): number | null {
  if (!raw) {
    return null;
  }
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Extracts filter parameters from URL search params */
export function extractMyJobsUrlParams(
  searchParams: URLSearchParams
): MyJobsUrlParams {
  const result: MyJobsUrlParams = {};

  // Extract basic search filters
  const companyName = searchParams.get(MyJobsQueryParamsEnum.COMPANY_NAME);
  const companyId = searchParams.get(MyJobsQueryParamsEnum.COMPANY_ID);
  const address = searchParams.get(MyJobsQueryParamsEnum.ADDRESS);
  const lat = searchParams.get(MyJobsQueryParamsEnum.ADDRESS_LAT);
  const lng = searchParams.get(MyJobsQueryParamsEnum.ADDRESS_LNG);
  const miles = searchParams.get(MyJobsQueryParamsEnum.MILES);

  if (companyName || companyId || address || miles) {
    // Reconstruct PlaceResult if we have address data
    const placeResult =
      address && lat && lng
        ? ({
            formatted_address: address,
            geometry: {
              location: {
                lat: () => Number(lat),
                lng: () => Number(lng),
              },
            },
          } as google.maps.places.PlaceResult)
        : null;

    result.searchFilters = {
      companyName: companyName || '',
      companyId: companyId || null,
      miles: miles ? Number(miles) : null,
      address: placeResult,
    };
  }

  // Extract sort/order
  const sort = searchParams.get(MyJobsQueryParamsEnum.SORT);
  const order = searchParams.get(MyJobsQueryParamsEnum.ORDER);
  if (sort) result.sort = sort;
  if (order) result.order = order;

  // Extract advanced filters
  const jobTitles = searchParams.get(MyJobsQueryParamsEnum.JOB_TITLES);
  const startDate = searchParams.get(MyJobsQueryParamsEnum.START_DATE);
  const endDate = searchParams.get(MyJobsQueryParamsEnum.END_DATE);
  const onGoing = searchParams.get(MyJobsQueryParamsEnum.ON_GOING);
  const minHourly = searchParams.get(MyJobsQueryParamsEnum.MIN_HOURLY_RATE);
  const minDaily = searchParams.get(MyJobsQueryParamsEnum.MIN_DAILY_RATE);
  const locationCountry = searchParams.get(
    MyJobsQueryParamsEnum.LOCATION_COUNTRY
  );
  const includeNational = searchParams.get(
    MyJobsQueryParamsEnum.LOCATION_INCLUDE_NATIONAL
  );

  if (
    jobTitles ||
    startDate ||
    endDate ||
    onGoing ||
    minHourly ||
    minDaily ||
    locationCountry ||
    includeNational
  ) {
    result.advancedFilters = {
      jobTitles: jobTitles ? jobTitles.split(',') : [],
      startDate: startDate ? new Date(startDate) : null,
      endDate: endDate ? new Date(endDate) : null,
      onGoing: onGoing === 'true',
      minHourlyEnabled: !!minHourly,
      minHourlyRate: minHourly ? Number(minHourly) : undefined,
      minDailyEnabled: !!minDaily,
      minDailyRate: minDaily ? Number(minDaily) : undefined,
      jobLocation: {
        country: (locationCountry as '' | CountryName) || '',
        includeNational: includeNational !== 'false', // Default to true
      },
    };
  }

  // Extract pagination
  const offset = searchParams.get(MyJobsQueryParamsEnum.OFFSET);
  const max = searchParams.get(MyJobsQueryParamsEnum.MAX);
  if (offset) result.offset = Number(offset);
  if (max) result.max = Number(max);

  result.openJobDetails = parseOpenJobDetailsId(
    searchParams.get(MyJobsQueryParamsEnum.OPEN_JOB_DETAILS)
  );

  return result;
}

/** View-state query params preserved when filter/search URL is rebuilt */
const MY_JOBS_VIEW_STATE_PARAMS: MyJobsQueryParamsEnum[] = [
  MyJobsQueryParamsEnum.OPEN_JOB_DETAILS,
];

export function getOpenJobDetailsId(
  searchParams: URLSearchParams
): number | null {
  return parseOpenJobDetailsId(
    searchParams.get(MyJobsQueryParamsEnum.OPEN_JOB_DETAILS)
  );
}

export function preserveMyJobsViewStateParams(
  source: URLSearchParams,
  target: URLSearchParams
): void {
  MY_JOBS_VIEW_STATE_PARAMS.forEach((key) => {
    const value = source.get(key);
    if (value != null) {
      target.set(key, value);
    }
  });
}

export function setOpenJobDetailsInUrl(
  searchParams: URLSearchParams,
  setSearchParams: (
    params: URLSearchParams,
    navigateOptions?: { replace?: boolean }
  ) => void,
  jobRecommendationId: number,
  options: { replace?: boolean } = {}
): void {
  const newParams = new URLSearchParams(searchParams);
  newParams.set(
    MyJobsQueryParamsEnum.OPEN_JOB_DETAILS,
    String(jobRecommendationId)
  );
  setSearchParams(newParams, { replace: options.replace ?? true });
}

export function clearOpenJobDetailsFromUrl(
  searchParams: URLSearchParams,
  setSearchParams: (
    params: URLSearchParams,
    navigateOptions?: { replace?: boolean }
  ) => void,
  options: { replace?: boolean } = {}
): void {
  if (!searchParams.has(MyJobsQueryParamsEnum.OPEN_JOB_DETAILS)) {
    return;
  }
  const newParams = new URLSearchParams(searchParams);
  newParams.delete(MyJobsQueryParamsEnum.OPEN_JOB_DETAILS);
  setSearchParams(newParams, { replace: options.replace ?? true });
}

/** Updates URL search params with filters */
export function handleMyJobsSearch(
  searchFilters: SearchFilters,
  advancedFilters: AdvancedFiltersFormValues,
  updateSearchParams: (params: URLSearchParams) => void,
  options: {
    sort?: string;
    order?: string;
    itemsPerPage?: number;
    existingSearchParams?: URLSearchParams;
  } = {}
): void {
  const { sort, order, itemsPerPage = MY_JOBS_PAGE_SIZE } = options;
  const newSearchParams = new URLSearchParams();

  // Add basic search filters
  if (searchFilters.companyName) {
    newSearchParams.set(
      MyJobsQueryParamsEnum.COMPANY_NAME,
      searchFilters.companyName
    );
  }

  if (searchFilters.companyId) {
    newSearchParams.set(
      MyJobsQueryParamsEnum.COMPANY_ID,
      searchFilters.companyId
    );
  }

  if (searchFilters.address) {
    const { address } = searchFilters;
    if (address.formatted_address) {
      newSearchParams.set(
        MyJobsQueryParamsEnum.ADDRESS,
        address.formatted_address
      );
    }
    if (address.geometry?.location) {
      const lat =
        typeof address.geometry.location.lat === 'function'
          ? address.geometry.location.lat()
          : address.geometry.location.lat;
      const lng =
        typeof address.geometry.location.lng === 'function'
          ? address.geometry.location.lng()
          : address.geometry.location.lng;

      if (lat !== undefined) {
        newSearchParams.set(MyJobsQueryParamsEnum.ADDRESS_LAT, lat.toString());
      }
      if (lng !== undefined) {
        newSearchParams.set(MyJobsQueryParamsEnum.ADDRESS_LNG, lng.toString());
      }
    }
  }

  if (searchFilters.miles) {
    newSearchParams.set(
      MyJobsQueryParamsEnum.MILES,
      searchFilters.miles.toString()
    );
  }

  // Add sort/order
  if (sort) {
    newSearchParams.set(MyJobsQueryParamsEnum.SORT, sort);
  }
  if (order) {
    newSearchParams.set(MyJobsQueryParamsEnum.ORDER, order);
  }

  // Add advanced filters
  if (advancedFilters.jobTitles.length > 0) {
    newSearchParams.set(
      MyJobsQueryParamsEnum.JOB_TITLES,
      advancedFilters.jobTitles.join(',')
    );
  }

  if (advancedFilters.startDate) {
    const startDate =
      advancedFilters.startDate instanceof Date
        ? advancedFilters.startDate
        : new Date(advancedFilters.startDate);
    newSearchParams.set(
      MyJobsQueryParamsEnum.START_DATE,
      startDate.toISOString()
    );
  }

  if (advancedFilters.endDate) {
    const endDate =
      advancedFilters.endDate instanceof Date
        ? advancedFilters.endDate
        : new Date(advancedFilters.endDate);
    newSearchParams.set(MyJobsQueryParamsEnum.END_DATE, endDate.toISOString());
  }

  if (advancedFilters.onGoing) {
    newSearchParams.set(MyJobsQueryParamsEnum.ON_GOING, 'true');
  }

  if (advancedFilters.minHourlyEnabled && advancedFilters.minHourlyRate) {
    newSearchParams.set(
      MyJobsQueryParamsEnum.MIN_HOURLY_RATE,
      advancedFilters.minHourlyRate.toString()
    );
  }

  if (advancedFilters.minDailyEnabled && advancedFilters.minDailyRate) {
    newSearchParams.set(
      MyJobsQueryParamsEnum.MIN_DAILY_RATE,
      advancedFilters.minDailyRate.toString()
    );
  }

  if (advancedFilters.jobLocation.country) {
    newSearchParams.set(
      MyJobsQueryParamsEnum.LOCATION_COUNTRY,
      advancedFilters.jobLocation.country
    );
  }
  if (advancedFilters.jobLocation.includeNational === false) {
    newSearchParams.set(
      MyJobsQueryParamsEnum.LOCATION_INCLUDE_NATIONAL,
      'false'
    );
  }

  // Reset pagination when searching
  newSearchParams.set(MyJobsQueryParamsEnum.OFFSET, '0');
  newSearchParams.set(MyJobsQueryParamsEnum.MAX, itemsPerPage.toString());

  if (options.existingSearchParams) {
    preserveMyJobsViewStateParams(
      options.existingSearchParams,
      newSearchParams
    );
  }

  updateSearchParams(newSearchParams);
}

/** Clears all URL search params */
export function handleMyJobsClear(
  updateSearchParams: (params: URLSearchParams) => void
): void {
  updateSearchParams(new URLSearchParams());
}

/** Converts frontend filters to API query params */
export function buildApiQueryParams(
  urlParams: MyJobsUrlParams
): MyJobsApiQueryParams {
  const apiParams: MyJobsApiQueryParams = {};

  // Pagination
  if (urlParams.offset !== undefined) {
    apiParams.offset = urlParams.offset;
  }
  if (urlParams.max !== undefined) {
    apiParams.max = urlParams.max;
  }

  // Basic filters
  if (urlParams.searchFilters) {
    const { companyId, address, miles } = urlParams.searchFilters;

    if (companyId) {
      apiParams.companyIds = companyId;
    }

    if (address?.formatted_address) {
      apiParams.address = address.formatted_address;
    }

    if (address?.geometry?.location) {
      const { location } = address.geometry;
      const lat =
        typeof location.lat === 'function' ? location.lat() : location.lat;
      const lng =
        typeof location.lng === 'function' ? location.lng() : location.lng;

      if (typeof lat === 'number') apiParams.addressLatitude = lat;
      if (typeof lng === 'number') apiParams.addressLongitude = lng;
    }

    if (miles) {
      apiParams.maxMilesAway = miles;
    }
  }

  // Sort/Order
  if (urlParams.sort) {
    apiParams.sort = urlParams.sort;
  }
  if (urlParams.order) {
    // Validate order is either 'asc' or 'desc'
    if (urlParams.order === 'asc' || urlParams.order === 'desc') {
      apiParams.order = urlParams.order;
    }
  }

  // Advanced filters
  if (urlParams.advancedFilters) {
    const {
      jobTitles,
      startDate,
      endDate,
      onGoing,
      minHourlyRate,
      minDailyRate,
      jobLocation,
    } = urlParams.advancedFilters;

    if (jobTitles && jobTitles.length > 0) {
      // Keep as array - buildURLSearchParams will append each with same key
      // This produces ?titleIds=1&titleIds=2 (Grails-compatible) instead of ?titleIds=1,2
      apiParams.titleIds = jobTitles;
    }

    if (startDate) {
      const date = startDate instanceof Date ? startDate : new Date(startDate);
      const [datePart] = date.toISOString().split('T');
      apiParams.startDate = datePart; // yyyy-MM-dd
    }

    if (endDate) {
      const date = endDate instanceof Date ? endDate : new Date(endDate);
      const [datePart] = date.toISOString().split('T');
      apiParams.endDate = datePart; // yyyy-MM-dd
    }

    if (onGoing !== undefined) {
      apiParams.onGoing = onGoing;
    }

    if (minHourlyRate !== undefined) {
      apiParams.minimumHourlyRate = minHourlyRate;
    }

    if (minDailyRate !== undefined) {
      apiParams.minimumDailyRate = minDailyRate;
    }

    // Determine location parameter based on job location filters
    // Matches public jobs implementation: radio for country + checkbox for national
    if (jobLocation) {
      // Map country radio selection (mutually exclusive)
      if (jobLocation.country === CountryName.UNITED_STATES) {
        apiParams.location = CountryName.UNITED_STATES;
      } else if (jobLocation.country === CountryName.CANADA) {
        apiParams.location = CountryName.CANADA;
      }
      // If country is empty string: don't set location param (returns both countries)

      // Map national checkbox
      // Backend defaults to true, only set if explicitly false
      if (jobLocation.includeNational === false) {
        apiParams.includeNationalJob = false;
      }
    }
  }

  return apiParams;
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/** Extracts an error message from an API response.
 *  Looks at `data.code` and `data.error` first (the {code, error} envelope used by
 *  newer endpoints — see web-app/CLAUDE.md "Error response" format), then falls
 *  back to `data.message` (legacy shape), then the raw `err.message`.
 */
export function extractErrorMessage(
  err: unknown,
  defaultMessage: string
): string {
  const error = err as {
    response?: { data?: { code?: string; error?: string; message?: string } };
    message?: string;
  };
  const data = error?.response?.data;

  return (
    data?.code ||
    data?.error ||
    data?.message ||
    error?.message ||
    defaultMessage
  );
}

/** Returns total count from API response (from pagination object) */
export function getTotalCount(
  data:
    | JobRecommendationResponse
    | AppliedJobsResponse
    | SavedJobsResponse
    | null
    | undefined
): number {
  return data?.pagination?.count || 0;
}

// ============================================================================
// Job Data Transformation
// ============================================================================

/**
 * Normalizes backend job DTO (title, company, dates)
 * @param idSource Which ID to use: recommendation, assignment, or saved
 */
export function normalizeJobFromDTO(
  result: WorkerJobCommonResultDTO,
  idSource: JobIdSource
): TransformedJobItem {
  const {
    job,
    company,
    jobRecommendation,
    jobAssignment,
    workerSavedJob,
    jobApplication,
    distance,
  } = result;

  let id: number;
  if (idSource === JobIdSource.RECOMMENDATION) {
    id = jobRecommendation?.id || 0;
  } else if (idSource === JobIdSource.ASSIGNMENT) {
    id = jobAssignment?.id || 0;
  } else {
    id = workerSavedJob?.savedJobId || 0;
  }

  const commonFields = {
    workerId: jobRecommendation?.workerId || jobAssignment?.workerId,
    savedAt: workerSavedJob?.savedAt || null,
    appliedAt: jobApplication?.dateApplied || null,
    distance:
      distance !== undefined && distance > 0
        ? Number(distance.toFixed(1))
        : null,
    jobAssignmentId: jobAssignment?.id ?? null,
    jobAssignmentStatus: jobAssignment?.status ?? null,
    isActive: result.isActive ?? true,
    canApply: result.canApply ?? true,
    canUnApply: result.canUnApply ?? false,
    hired: jobAssignment?.hired ?? false,
  };

  if (!job) {
    return {
      id,
      job: null,
      jobId: undefined,
      ...commonFields,
    };
  }

  const jobCompany = company || job.company;
  if (!jobCompany) {
    // If no company is available, we can't render the item correctly.
    return {
      id,
      job: null,
      jobId: undefined,
      ...commonFields,
    };
  }

  const normalizedJob: NormalizedJobDetail = {
    ...job,
    // Title normalization: object → string
    title: typeof job.title === 'string' ? job.title : job.title?.name || '',
    // Company merging: prefer parent company over job.company
    company: jobCompany,
    // Date field normalization: start/end OR startDate/endDate → always startDate/endDate
    startDate: job.jobStart || job.start || '',
    endDate: job.jobEnd || job.end || '',
  };

  return {
    id,
    job: normalizedJob,
    jobId: job.id,
    ...commonFields,
  };
}

/** Transforms NEW tab response to job items */
export function transformNewJobRecommendations(
  newJobsData: JobRecommendationResponse | null | undefined
): TransformedJobItem[] | null {
  if (!newJobsData?.results) {
    return null;
  }

  return newJobsData.results.map((result) =>
    normalizeJobFromDTO(result, JobIdSource.RECOMMENDATION)
  );
}

/** Transforms APPLIED tab response to job items */
export function transformAppliedJobsToRecommendations(
  appliedJobsData: AppliedJobsResponse | null | undefined
): TransformedJobItem[] | null {
  if (!appliedJobsData?.results) {
    return null;
  }

  return appliedJobsData.results.map((result) =>
    normalizeJobFromDTO(result, JobIdSource.ASSIGNMENT)
  );
}

/** Status-derived flags for a recommendation. Returns safe defaults when null. */
export function getRecommendationFlags(rec: TransformedJobItem | null) {
  return {
    job: rec?.job ?? null,
    jobAssignmentId: rec?.jobAssignmentId ?? null,
    isApplied: !!rec?.appliedAt,
    isPastJob: rec?.isActive === false,
    isOffered: rec?.jobAssignmentStatus === JobAssignmentStatus.OFFERED,
    isHired:
      rec?.hired === true ||
      rec?.jobAssignmentStatus === JobAssignmentStatus.HIRED,
    isCancelled:
      rec?.jobAssignmentStatus === JobAssignmentStatus.CANCELLED_BY_WORKER ||
      rec?.jobAssignmentStatus === JobAssignmentStatus.CANCELLED_BY_COMPANY,
  };
}

// ============================================================================
// Save/Unsave Job Helpers
// ============================================================================

/**
 * Updates workerSavedJob for optimistic save/unsave (works across all tabs)
 * @param savedAt ISO timestamp or null to unsave
 */
export function updateWorkerJobSavedState(
  data:
    | import('@worker/types/myJobs.type').WorkerJobCommonResultsResponse
    | undefined,
  jobId: number,
  savedAt: string | null
):
  | import('@worker/types/myJobs.type').WorkerJobCommonResultsResponse
  | undefined {
  if (!data?.results) return data;

  return {
    ...data,
    results: data.results.map((result) =>
      result.job?.id === jobId
        ? {
            ...result,
            workerSavedJob: savedAt
              ? {
                  savedJobId: result.workerSavedJob?.savedJobId || 0,
                  jobId,
                  savedAt,
                }
              : undefined, // Clear when unsaving
          }
        : result
    ),
  };
}

// ============================================================================
// Saved Jobs Transformation
// ============================================================================

/** Transforms SAVED tab response to job items */
export function transformSavedJobsToRecommendations(
  savedJobsData: SavedJobsResponse | null | undefined
): TransformedJobItem[] | null {
  if (!savedJobsData?.results) {
    return null;
  }

  return savedJobsData.results.map((result) =>
    normalizeJobFromDTO(result, JobIdSource.SAVED)
  );
}

// ============================================================================
// Apply/Unapply Job Helpers
// ============================================================================

/** Removes job from APPLIED tab cache (optimistic update) */
export function removeAppliedJob(
  data: AppliedJobsResponse | undefined,
  jobId: number
): AppliedJobsResponse | undefined {
  if (!data?.results) return data;

  const newCount = Math.max(0, (data.pagination?.count || 0) - 1);
  return {
    ...data,
    pagination: { ...data.pagination, count: newCount },
    results: data.results.filter((result) => result.job?.id !== jobId),
  };
}

/** Removes job from SAVED tab cache when unsaved */
export function removeSavedJob(
  data: SavedJobsResponse | undefined,
  jobId: number
): SavedJobsResponse | undefined {
  if (!data?.results) return data;

  const newCount = Math.max(0, (data.pagination?.count || 0) - 1);
  return {
    ...data,
    pagination: { ...data.pagination, count: newCount },
    results: data.results.filter((result) => result.job?.id !== jobId),
  };
}

// ============================================================================
// Availability Helpers
// ============================================================================

/** Transforms BE availability response to component-friendly entries */
export const transformWorkerAvailabilityToEntries = (
  apiData: WorkerAvailabilityResponse[],
  timezoneId?: string
): AvailabilityEntry[] => {
  return apiData
    .filter((item) => item.startDate && item.endDate)
    .flatMap((item) => {
      let startDate: Date;
      let endDate: Date;

      let localStartTime: string | undefined;
      let localEndTime: string | undefined;

      if (item.isAllDay) {
        startDate = parseISO(item.startDate);
        endDate = parseISO(item.endDate);
      } else {
        // Skip entries with missing times — prevents Invalid Date crash
        if (!item.startTime || !item.endTime) return [];

        // API returns dates/times in UTC — convert to worker's local timezone
        const startUTC = new Date(`${item.startDate}T${item.startTime}:00Z`);
        const endUTC = new Date(`${item.endDate}T${item.endTime}:00Z`);

        if (!isValid(startUTC) || !isValid(endUTC)) return [];

        if (timezoneId) {
          startDate = parseISO(
            formatInTimeZone(startUTC, timezoneId, 'yyyy-MM-dd')
          );
          endDate = parseISO(
            formatInTimeZone(endUTC, timezoneId, 'yyyy-MM-dd')
          );
          localStartTime = formatInTimeZone(startUTC, timezoneId, 'HH:mm');
          localEndTime = formatInTimeZone(endUTC, timezoneId, 'HH:mm');
        } else {
          startDate = parseISO(item.startDate);
          endDate = parseISO(item.endDate);
          localStartTime = item.startTime;
          localEndTime = item.endTime;
        }
      }

      if (!isValid(startDate) || !isValid(endDate)) return [];

      return [
        {
          id: item.id,
          startDate,
          endDate,
          isAllDay: item.isAllDay,
          startTime: localStartTime,
          endTime: localEndTime,
        },
      ];
    });
};

// ============================================================================
// Apply/Unapply Job Helpers
// ============================================================================

/** Updates job application AND assignment status (keeps job in list) - works for NEW, SAVED, and APPLIED tabs */
export function updateJobApplicationStatus(
  data: WorkerJobCommonResultsResponse | undefined,
  jobId: number,
  applicationData: JobApplication | undefined | null,
  assignmentData?: JobAssignment | undefined | null
): WorkerJobCommonResultsResponse | undefined {
  if (!data?.results) return data;

  const hasApplication = applicationData != null;

  return {
    ...data,
    results: data.results.map((result) =>
      result.job?.id === jobId
        ? {
            ...result,
            jobApplication:
              applicationData === null ? undefined : applicationData,
            // Update jobAssignment if provided (for apply/unapply chat status sync)
            ...(assignmentData !== undefined && {
              jobAssignment:
                assignmentData === null ? undefined : assignmentData,
            }),
            canApply: !hasApplication,
            canUnApply: hasApplication,
          }
        : result
    ),
  };
}
